import { useCallback, useMemo } from 'react'
import { Badge, Button, Empty, Table, Tabs } from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import { List, useTable } from '@refinedev/antd'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'

import type { ReturnRoot } from '@repo/sdk'

import { isReturnStatus, useReturnStatusConfig, type ReturnStatus } from './returnStatusConfig'
import { bindPaginationToUrl, useSyncTableWithUrl } from '../../shared/hooks/useSyncTableWithUrl'
import { useReturnTabCounts } from './useReturnTabCounts'
import { useReturnReasonLabels } from './returnReasonConfig'
import { createReturnListColumns } from './ReturnListColumns'
import { useReturnListUrlSync } from './useReturnListUrlSync'
import { createRowClickHandler } from '@/utils/createRowClickHandler'
import { scrollXForColumns } from '@/components/responsive'

const RETURN_STATUSES: ReturnStatus[] = ['REQUESTED', 'APPROVED', 'REJECTED']

export function ReturnList() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const statusConfig = useReturnStatusConfig()
  const reasonLabels = useReturnReasonLabels()
  const { counts } = useReturnTabCounts()
  const { activeTab, currentPage: urlPage, setTab, setPage } = useReturnListUrlSync()

  const { tableProps, tableQuery, setFilters, currentPage, setCurrentPage } = useTable<ReturnRoot>({
    resource: 'return-list',
    pagination: { pageSize: 20, currentPage: urlPage },
    syncWithLocation: false,
    filters: {
      initial: [{ field: 'status', operator: 'eq', value: activeTab }],
    },
  })

  const filters = useMemo(
    () => [{ field: 'status', operator: 'eq' as const, value: activeTab }],
    [activeTab],
  )
  useSyncTableWithUrl({ filters, urlPage, currentPage, setFilters, setCurrentPage })

  const handleTabChange = useCallback(
    (key: string) => {
      if (!isReturnStatus(key)) return
      setTab(key)
    },
    [setTab],
  )

  const handleViewDetail = useCallback(
    (returnId: string, orderId: string) => {
      void navigate(`/returns/${returnId}?orderId=${orderId}`)
    },
    [navigate],
  )

  // Re-setting identical filters does not change refine's content-hashed query key,
  // so the old "refresh" never refetched anything.
  const handleRefresh = useCallback(() => void tableQuery.refetch(), [tableQuery])

  const columns = useMemo(
    () =>
      createReturnListColumns({ onViewDetail: handleViewDetail, t, statusConfig, reasonLabels }),
    [handleViewDetail, t, statusConfig, reasonLabels],
  )

  const tabItems = RETURN_STATUSES.map((status) => ({
    key: status,
    label: (
      <span data-testid={`return-list-tab-${status.toLowerCase()}`}>
        {statusConfig[status].label}{' '}
        <Badge count={counts[status]} showZero={false} overflowCount={999} />
      </span>
    ),
  }))

  return (
    <List
      title={t('pages.returns.listTitle')}
      headerButtons={
        <Button
          data-testid="return-list-refresh-btn"
          icon={<ReloadOutlined />}
          onClick={handleRefresh}
        >
          {t('common.buttons.refresh')}
        </Button>
      }
    >
      <Tabs activeKey={activeTab} onChange={handleTabChange} items={tabItems} />
      <Table
        {...tableProps}
        loading={tableQuery.isFetching}
        pagination={bindPaginationToUrl(tableProps.pagination, urlPage, setPage)}
        columns={columns}
        rowKey="returnId"
        scroll={{ x: scrollXForColumns(columns, 1250) }}
        locale={{
          emptyText: (
            <Empty
              description={t('pages.returns.emptyState')}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ),
        }}
        onRow={(record) => ({
          'data-testid': `return-list-row-${record.returnId}`,
          onClick: createRowClickHandler(
            () => void navigate(`/returns/${record.returnId}?orderId=${record.order.orderId}`),
          ),
          style: { cursor: 'pointer' },
        })}
      />
    </List>
  )
}
